/**
 * T087: Tag Filter Component
 * Multi-select filter for filtering prompts by tags
 */

import { X } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useTags } from '@/hooks/useTags'
import { Skeleton } from '@/components/ui/skeleton'

interface TagFilterProps {
  value: string[]
  onChange: (value: string[]) => void
}

export function TagFilter({ value, onChange }: TagFilterProps) {
  const { data, isLoading } = useTags()
  const tags = data?.data ?? []

  const selectedTags = tags.filter((tag) => value.includes(tag.id))
  const availableTags = tags.filter((tag) => !value.includes(tag.id))

  const handleAdd = (tagId: string) => {
    if (tagId && !value.includes(tagId)) {
      onChange([...value, tagId])
    }
  }

  const handleRemove = (tagId: string) => {
    onChange(value.filter((id) => id !== tagId))
  }

  if (isLoading) {
    return <Skeleton className="h-10 w-40" />
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {selectedTags.map((tag) => (
        <Badge key={tag.id} variant="secondary" className="gap-1">
          {tag.name}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-auto p-0 hover:bg-transparent"
            onClick={() => handleRemove(tag.id)}
          >
            <X className="h-3 w-3" />
            <span className="sr-only">Remove {tag.name}</span>
          </Button>
        </Badge>
      ))}
      {availableTags.length > 0 && (
        <Select onValueChange={handleAdd} value="">
          <SelectTrigger className="h-8 w-32">
            <SelectValue placeholder="Add tag..." />
          </SelectTrigger>
          <SelectContent>
            {availableTags.map((tag) => (
              <SelectItem key={tag.id} value={tag.id}>
                {tag.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  )
}
